import { Linkedin, Twitter } from 'lucide-react';
import { LogPulseLogo } from './LogPulseLogo';
import { LocalizedAnchor } from './LocalizedLink';
import { useT } from '@/i18n/provider';
import { DISCORD_INVITE_URL, DISCORD_LIVE } from '@/lib/site-config';

function DiscordIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}

export function LandingFooter() {
  const t = useT();

  const links = [
    {
      title: t('footer.product'),
      items: [
        { label: t('footer.features'), href: '/#features' },
        { label: t('footer.siem'), href: '/siem' },
        { label: t('footer.serviceIntelligence'), href: '/service-intelligence' },
        { label: t('footer.aiSearch'), href: '/ai-log-search' },
        { label: 'MCP server', href: '/mcp' },
        { label: 'Close the loop', href: '/close-the-loop' },
        { label: t('footer.fleet'), href: '/agents' },
        { label: t('footer.pricing'), href: '/pricing' },
        { label: t('footer.docs'), href: '/docs' },
        { label: 'Changelog', href: '/changelog' },
      ],
    },
    {
      title: t('footer.solutions'),
      items: [
        { label: 'Agentic SOC', href: '/agentic-soc' },
        { label: 'Threat intelligence', href: '/threat-intelligence' },
        { label: 'NIS2 compliance', href: '/compliance/nis2' },
        { label: 'DORA compliance', href: '/compliance/dora' },
        { label: 'ClickHouse log management', href: '/clickhouse-log-management' },
        { label: t('footer.topdesk'), href: '/integrations/topdesk' },
        { label: t('footer.servicenow'), href: '/integrations/servicenow' },
        { label: 'Compare', href: '/compare' },
        { label: 'Alternatives', href: '/alternatives' },
      ],
    },
    {
      title: t('footer.company'),
      items: [
        { label: t('footer.about'), href: '/about' },
        { label: 'Engineering Blog', href: '/blog' },
        { label: 'Guides', href: '/guides' },
        { label: 'Glossary', href: '/glossary' },
        { label: 'Join the Community', href: '/community' },
        { label: t('footer.contact'), href: '/contact' },
        { label: t('footer.reseller'), href: '/reseller' },
        { label: 'Security', href: '/security' },
        { label: t('footer.architecture'), href: '/architecture' },
        { label: t('footer.status'), href: '/status' },
      ],
    },
    {
      title: t('footer.legal'),
      items: [
        { label: t('footer.privacy'), href: '/privacy' },
        { label: t('footer.terms'), href: '/terms' },
        { label: t('footer.cookies'), href: '/cookies' },
        { label: t('footer.imprint'), href: '/imprint' },
      ],
    },
  ];

  return (
    <footer className="bg-gray-950 text-gray-400 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5">
              <LogPulseLogo className="h-7 w-7" />
              <span className="text-lg font-bold text-white">LogPulse</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            <div className="mt-4 flex items-center gap-3">
              {DISCORD_LIVE && (
                <a
                  href={DISCORD_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-white transition-colors"
                  aria-label="LogPulse on Discord"
                >
                  <DiscordIcon className="h-5 w-5" />
                </a>
              )}
              <a
                href="https://x.com/logpulse_io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-white transition-colors"
                aria-label="LogPulse on X"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://linkedin.com/company/logpulse"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-white transition-colors"
                aria-label="LogPulse on LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {links.map((group) => (
            <div key={group.title}>
              <p className="text-sm font-semibold text-white mb-4">
                {group.title}
              </p>
              <ul className="space-y-2.5">
                {group.items.map((item) => (
                  <li key={item.label}>
                    <LocalizedAnchor
                      href={item.href}
                      className="text-sm hover:text-white transition-colors"
                    >
                      {item.label}
                    </LocalizedAnchor>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar. The registered entity is named here as well as in the
            colophon: LogPulse is a trade name, so the two differ. */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-sm flex flex-col items-center gap-y-2 text-center">
          <span>{t('footer.copyright')}</span>
          <span className="text-xs text-gray-500">{t('footer.legalEntity')}</span>
        </div>
      </div>
    </footer>
  );
}
